import React, { Fragment, useCallback, useContext } from "react";

import { Divider } from "@magnetic/divider";
import { Header } from "@magnetic/header";
import { useZIndex } from "@magnetic/z-index";

import { CiscoLogo } from "src/common";
import { DevMonitor } from "src/core/appHeader/devMonitor";
import { UserProfile } from "src/core/appHeader/userProfile";
import C3Header, { C3AIAssistant, C3AICanvas } from "src/core/c3Header";
import { LoginContext } from "src/core/loginContext";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { MockShellGlobalHeader } from "src/pages/mock-shell/mockShellGlobalHeader";
import { Constants } from "src/utils/constants";

import "src/core/appHeader/appHeader.scss";

interface HelpContentMetadata {
  label: string;
  url: string;
}

const AppHeaderBranding = () => {
  const { session } = useContext(LoginContext);
  const isDevMode = window.cnc.settings.get("dev");
  const productName = `${Constants.APP_NAME}${
    isDevMode ? ` - Developer Mode - ${__APP_VERSION__}` : ""
  }`;

  if (session?.isC3Crosslaunch) {
    return (
      <div className="c3-header app-header-c3-header">
        <C3Header />
      </div>
    );
  }

  return (
    <>
      <div className="mds-header-branding-logo">
        <a href="/">
          <CiscoLogo />
        </a>
      </div>
      <div className="mds-header-branding-name">
        <a href="/">{productName}</a>
      </div>
    </>
  );
};

const helpContentMetadataList: Array<Array<HelpContentMetadata>> = [
  [
    {
      label: "Release notes",
      url: "//www.cisco.com/c/en/us/docs/technical-documentation.html?product=N4IgTgpgzgrgNgFxALlABzAewCYwMYIByAhgLYQogDCAllHpgASEQAeMUjAEgJ5oRgAZsQBGYGnhAAaEAhoI4FZCBbtOvfkNHjJMgHY4IASWyUATAA4AbAGYbAdgAMVq9JBpikPUUMnz1m0cLexsATjdSbEEqYgQIAHNMMB5KAAUsXAIoNzgaQQgqHjxFSmICGgA3Cn1DABU%2BJRAoARpoNwNsCABZKK6IBGIqOGIobOUAZUxBBAB3TwhGADEyGjgUmWw6NGGeABk8gqKS5TcITAQAEUw8MccAXzugA&selectedFilters=N4IgZglgNgLgpgJwM4gFygCYHsDGBhAQ3gHMsEI4VUBtEAJTijgKTgAIBJAOzDIFsiELFxABdADQgEjZqyq0AglChiAvpKRkYAIQCeaEFzgB3SjBCqgA"
    },
    {
      label: "Get started",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/collections/hyperfabric-user-content.html#expanded-tabs-d3d0761755-item-8d8162e45a-tabpanel"
    },
    {
      label: "Subscriptions",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/software/cisco-nexus-hyperfabric-subscription.html"
    },
    {
      label: "Deploy your fabric on site",
      url: "//www.cisco.com/c/en/us/td/docs/dcn/hyperfabric/software/cisco-nexus-hyperfabric-on-site.html"
    }
  ],
  [
    {
      label: "AI assistant for technical documentation",
      url: "//www.cisco.com/c/en/us/docs/technical-documentation.html?product=N4IgTgpgzgrgNgFxALlABzAewCYwMYIByAhgLYQogDCAllHpgASEQAeMUjAEgJ5oRgAZsQBGYGnhAAaEAhoI4FZCBbtOvfkNHjJMgHY4IASWyUATAA4AbAGYbAdgAMVq9JBpikPUUMnz1m0cLexsATjdSbEEqYgQIAHNMMB5KAAUsXAIoNzgaQQgqHjxFSmICGgA3Cn1DABU%2BJRAoARpoNwNsCABZKK6IBGIqOGIobOUAZUxBBAB3TwhGADEyGjgUmWw6NGGeABk8gqKS5TcITAQAEUw8MccAXzugA"
    }
  ]
] as const;

const HelpMenu = () => {
  const openHelpContentUrl = useCallback((item: HelpContentMetadata) => {
    window.open(item.url, "_blank");
  }, []);

  return (
    <Header.Menu icon="question" label="Help">
      {helpContentMetadataList.map((helpContentGroup, immutableGroupIndex) => (
        <Fragment key={+immutableGroupIndex}>
          {helpContentGroup.map((helpContentMetadata) => (
            <Header.MenuItem
              key={helpContentMetadata.label}
              onClick={() => openHelpContentUrl(helpContentMetadata)}
              label={helpContentMetadata.label}
              href="#"
            />
          ))}
          {immutableGroupIndex < helpContentMetadataList.length - 1 && (
            <Header.MenuDivider />
          )}
        </Fragment>
      ))}
    </Header.Menu>
  );
};

const PendingFabricChanges = () => {
  const { events, fabric } = useContext(DataProviderContext);
  if (fabric?.id) {
    return (
      <Header.Dropdown
        className="pending-changes"
        icon="deploy"
        label="Pending changes"
        panel={false}
        alert={events?.length || 0}
      >
        <></>
      </Header.Dropdown>
    );
  }
  return null;
};

const HeaderDivider = () => {
  return (
    <Divider
      className="mds-header-alert-divider app-header-divider"
      direction="vertical"
    />
  );
};

export const AppHeader = () => {
  const { session } = useContext(LoginContext);
  const headerZIndex = useZIndex("header");

  return (
    <header
      className="mds-rebuild-header app-header"
      style={{ zIndex: headerZIndex }}
      title={session?.tenantName}
    >
      <div className="mds-header-left">
        <AppHeaderBranding />
      </div>
      <div className="mds-header-right">
        <div className="mds-header-right-content">
          {session?.isC3Crosslaunch && (
            <>
              <div className="c3-header app-header-c3-ai-tools">
                <C3AIAssistant />
                <C3AICanvas />
              </div>
              <HeaderDivider />
            </>
          )}
          <HelpMenu />
          <MockShellGlobalHeader />
          <DevMonitor />
          <PendingFabricChanges />
        </div>
        <HeaderDivider />
        <UserProfile />
      </div>
    </header>
  );
};
